import React, { useState, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { ArrowDropDownIcon, Box, BoxProps, Input, Text } from '@pancakeswap/uikit'

const DropDownHeader = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 16px;
  box-shadow: ${({ theme }) => theme.shadows.inset};
  border: 1px solid ${({ theme }) => theme.colors.inputSecondary};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.input};
  transition: border-radius 0.15s;
`

const DropDownListContainer = styled.div`
  min-width: 136px;
  height: 0;
  max-height: 0;
  position: absolute;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.input};
  z-index: ${({ theme }) => theme.zIndices.dropdown};
  transition: transform 0.15s, opacity 0.15s;
  transform: scaleY(0);
  transform-origin: top;
  opacity: 0;
  width: 100%;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: 168px;
  }
`

const DropDownContainer = styled(Box)<{ isOpen: boolean }>`
  cursor: pointer;
  width: 100%;
  position: relative;
  background: ${({ theme }) => theme.colors.input};
  border-radius: 16px;
  height: 40px;
  min-width: 136px;
  user-select: none;
  z-index: 20;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: 168px;
  }

  ${(props) =>
    props.isOpen &&
    css`
      ${DropDownHeader} {
        border-bottom: 1px solid ${({ theme }) => theme.colors.inputSecondary};
        box-shadow: ${({ theme }) => theme.tooltip.boxShadow};
        border-radius: 16px 16px 0 0;
      }

      ${DropDownListContainer} {
        height: auto;
        max-height: 60vh;
        transform: scaleY(1);
        opacity: 1;
        border: 1px solid ${({ theme }) => theme.colors.inputSecondary};
        border-top-width: 0;
        border-radius: 0 0 16px 16px;
        box-shadow: ${({ theme }) => theme.tooltip.boxShadow};
        overflow-y: auto;
      }
    `}

  svg {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
  }
`

const DropDownList = styled.ul`
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  z-index: ${({ theme }) => theme.zIndices.dropdown};
  ${({ theme }) => theme.mediaQueries.sm} {
    max-height: 360px;
  }
`

const SearchBox = styled.div`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.input};
  padding: 8px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.inputSecondary};
  z-index: 1;
`

const SearchInput = styled(Input)`
  width: 100%;
  height: 32px;
`

const ListItem = styled.li`
  list-style: none;
  padding: 8px 16px;
  &:hover {
    background: ${({ theme }) => theme.colors.inputSecondary};
  }
`

export interface SelectProps extends BoxProps {
  options: OptionProps[]
  onOptionChange?: (option: OptionProps) => void
  defaultOptionIndex?: number
  searchable?: boolean
  searchPlaceholder?: string
  searchPredicate?: (option: OptionProps, query: string) => boolean
}

export interface OptionProps {
  label: React.ReactNode
  value: any
  searchText?: string
}

const Select: React.FunctionComponent<SelectProps> = ({
  options,
  onOptionChange,
  defaultOptionIndex = 0,
  searchable = false,
  searchPlaceholder = 'Search',
  searchPredicate,
  ...props
}) => {
  const dropdownRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(defaultOptionIndex)
  const [searchQuery, setSearchQuery] = useState('')

  const renderLabel = (label: React.ReactNode) => {
    return typeof label === 'string' ? <Text>{label}</Text> : label
  }

  const toggling = (event: React.MouseEvent<HTMLDivElement>) => {
    setIsOpen(!isOpen)
    event.stopPropagation()
  }

  const onOptionClicked = (selectedIndex: number) => () => {
    setSelectedOptionIndex(selectedIndex)
    setIsOpen(false)

    if (onOptionChange) {
      onOptionChange(options[selectedIndex])
    }
  }

  useEffect(() => {
    const handleClickOutside = () => {
      setIsOpen(false)
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
    }
  }, [isOpen])

  useEffect(() => {
    if (options.length === 0) return
    if (selectedOptionIndex >= options.length) {
      setSelectedOptionIndex(options.length - 1)
    }
  }, [options.length, selectedOptionIndex])

  const normalizedQuery = searchQuery.trim().toLowerCase()
  const filteredOptions = options
    .map((option, index) => ({ option, index }))
    .filter(({ option }) => {
      if (!normalizedQuery) return true
      if (searchPredicate) return searchPredicate(option, normalizedQuery)
      const fallbackText =
        option.searchText ||
        (typeof option.label === 'string' ? option.label : '') ||
        (typeof option.value === 'string' ? option.value : '')
      return fallbackText.toLowerCase().includes(normalizedQuery)
    })
  const visibleOptions = filteredOptions.filter(({ index }) => index !== selectedOptionIndex)

  const selectedOption = options[selectedOptionIndex]

  return (
    <DropDownContainer isOpen={isOpen} {...props}>
      <DropDownHeader onClick={toggling}>
        {selectedOption ? renderLabel(selectedOption.label) : <Text>-</Text>}
      </DropDownHeader>
      <ArrowDropDownIcon color="text" onClick={toggling} />
      <DropDownListContainer onClick={(event) => event.stopPropagation()}>
        {searchable && (
          <SearchBox>
            <SearchInput
              scale="sm"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </SearchBox>
        )}
        <DropDownList ref={dropdownRef}>
          {visibleOptions.length > 0 ? (
            visibleOptions.map(({ option, index }) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem onClick={onOptionClicked(index)} key={`${option.value}-${index}`}>
                {renderLabel(option.label)}
              </ListItem>
            ))
          ) : (
            <ListItem>
              <Text color="textSubtle">No results</Text>
            </ListItem>
          )}
        </DropDownList>
      </DropDownListContainer>
    </DropDownContainer>
  )
}

export default Select
